/**
 * Serviço HTTP
 * Responsável por gerenciar comunicações HTTP do SDK
 */

export class HttpService {
    constructor(config, logger, errorHandler) {
        this.config = config;
        this.logger = logger.child('HttpService');
        this.errorHandler = errorHandler;
        
        this.stats = {
            requestsSent: 0,
            requestsSuccessful: 0,
            requestsFailed: 0,
            averageResponseTime: 0,
            lastRequestTime: null,
            retryAttempts: 0,
            bytesTransferred: 0
        };
        
        this.requestQueue = [];
        this.isProcessingQueue = false;
        this.maxRetries = config.maxRetries || 3;
        this.retryDelay = config.retryDelay || 1000;
        this.timeout = config.timeout || 10000;
        this.maxQueueSize = config.maxQueueSize || 100;
        
        // Headers padrão
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-SDK-Version': config.version || '1.0.0',
            'X-Customer-ID': config.customerId
        };
        
        // Interceptors
        this.requestInterceptors = [];
        this.responseInterceptors = [];
    }
    
    /**
     * Envia fingerprint para o servidor
     */
    async sendFingerprint(fingerprint, options = {}) {
        const startTime = performance.now();
        
        try {
            const payload = {
                fingerprint,
                metadata: {
                    timestamp: Date.now(),
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    referrer: document.referrer,
                    customerId: this.config.customerId,
                    sessionId: this.config.sessionId
                }
            };
            
            const response = await this.request('POST', this.config.apiUrl, payload, {
                priority: 'high',
                ...options
            });
            
            const duration = performance.now() - startTime;
            this.logger.info('Fingerprint enviado com sucesso', {
                duration: Math.round(duration),
                responseStatus: response.status,
                fingerprintHash: fingerprint.hash
            });
            
            return response;
            
        } catch (error) {
            const duration = performance.now() - startTime;
            this.logger.error('Erro ao enviar fingerprint', {
                error: error.message,
                duration: Math.round(duration),
                fingerprintHash: fingerprint.hash
            });
            throw error;
        }
    }
    
    /**
     * Envia dados comportamentais
     */
    async sendBehavioralData(behavioralData, options = {}) {
        try {
            const payload = {
                behavioralData,
                metadata: {
                    timestamp: Date.now(),
                    customerId: this.config.customerId,
                    sessionId: this.config.sessionId
                }
            };
            
            const response = await this.request('POST', `${this.config.apiUrl}/behavioral`, payload, {
                priority: 'medium',
                ...options
            });
            
            this.logger.debug('Dados comportamentais enviados', {
                eventsCount: behavioralData.events?.length || 0,
                responseStatus: response.status
            });
            
            return response;
            
        } catch (error) {
            this.logger.error('Erro ao enviar dados comportamentais', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Requisição HTTP genérica
     */
    async request(method, url, data = null, options = {}) {
        const requestId = this.generateRequestId();
        const startTime = performance.now();
        
        this.stats.requestsSent++;
        
        try {
            // Aplica interceptors de request
            const requestConfig = await this.applyRequestInterceptors({
                method,
                url,
                data,
                headers: { ...this.defaultHeaders, ...options.headers },
                timeout: options.timeout || this.timeout,
                requestId
            });
            
            // Executa requisição
            const response = await this.executeRequest(requestConfig, options);
            
            // Aplica interceptors de response
            const processedResponse = await this.applyResponseInterceptors(response, requestConfig);
            
            // Atualiza estatísticas
            const duration = performance.now() - startTime;
            this.updateStats(duration, true, response.data ? JSON.stringify(response.data).length : 0);
            
            this.logger.debug('Requisição HTTP concluída', {
                method,
                url,
                status: response.status,
                duration: Math.round(duration),
                requestId
            });
            
            return processedResponse;
            
        } catch (error) {
            const duration = performance.now() - startTime;
            this.updateStats(duration, false, 0);
            
            this.logger.error('Erro na requisição HTTP', {
                method,
                url,
                error: error.message,
                duration: Math.round(duration),
                requestId
            });
            
            throw error;
        }
    }
    
    /**
     * Executa requisição HTTP
     */
    async executeRequest(config, options = {}) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), config.timeout);
        
        try {
            const fetchOptions = {
                method: config.method,
                headers: config.headers,
                signal: controller.signal
            };
            
            if (config.data && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
                fetchOptions.body = JSON.stringify(config.data);
            }
            
            const response = await fetch(config.url, fetchOptions);
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const contentType = response.headers.get('content-type');
            let data = null;
            
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = await response.text();
            }
            
            return {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                data,
                config
            };
            
        } catch (error) {
            clearTimeout(timeoutId);
            
            if (error.name === 'AbortError') {
                throw new Error(`Timeout na requisição para ${config.url}`);
            }
            
            // Retry logic
            if (options.retry !== false && this.shouldRetry(error, config)) {
                return await this.retryRequest(config, options);
            }
            
            throw error;
        }
    }
    
    /**
     * Verifica se deve tentar novamente
     */
    shouldRetry(error, config) {
        // Não tenta novamente se já excedeu o limite
        if ((config.retryCount || 0) >= this.maxRetries) {
            return false;
        }
        
        // Não tenta novamente para erros de cliente (4xx)
        if (error.message.includes('HTTP 4')) {
            return false;
        }
        
        // Tenta novamente para erros de rede e servidor (5xx)
        return true;
    }
    
    /**
     * Tenta requisição novamente
     */
    async retryRequest(config, options) {
        config.retryCount = (config.retryCount || 0) + 1;
        this.stats.retryAttempts++;
        
        const delay = this.retryDelay * Math.pow(2, config.retryCount - 1); // Exponential backoff
        
        this.logger.warn('Tentando requisição novamente', {
            url: config.url,
            attempt: config.retryCount,
            delay
        });
        
        await this.sleep(delay);
        return await this.executeRequest(config, options);
    }
    
    /**
     * Adiciona requisição à fila
     */
    async queueRequest(method, url, data = null, options = {}) {
        if (this.requestQueue.length >= this.maxQueueSize) {
            throw new Error('Fila de requisições cheia');
        }
        
        return new Promise((resolve, reject) => {
            this.requestQueue.push({
                method,
                url,
                data,
                options,
                resolve,
                reject,
                timestamp: Date.now()
            });
            
            this.processQueue();
        });
    }
    
    /**
     * Processa fila de requisições
     */
    async processQueue() {
        if (this.isProcessingQueue || this.requestQueue.length === 0) {
            return;
        }
        
        this.isProcessingQueue = true;
        
        try {
            while (this.requestQueue.length > 0) {
                const request = this.requestQueue.shift();
                
                try {
                    const response = await this.request(
                        request.method,
                        request.url,
                        request.data,
                        request.options
                    );
                    request.resolve(response);
                } catch (error) {
                    request.reject(error);
                }
                
                // Pequeno delay entre requisições
                await this.sleep(100);
            }
        } finally {
            this.isProcessingQueue = false;
        }
    }
    
    /**
     * Aplica interceptors de request
     */
    async applyRequestInterceptors(config) {
        let processedConfig = config;
        
        for (const interceptor of this.requestInterceptors) {
            try {
                processedConfig = await interceptor(processedConfig);
            } catch (error) {
                this.logger.warn('Erro em interceptor de request', { error: error.message });
            }
        }
        
        return processedConfig;
    }
    
    /**
     * Aplica interceptors de response
     */
    async applyResponseInterceptors(response, config) {
        let processedResponse = response;
        
        for (const interceptor of this.responseInterceptors) {
            try {
                processedResponse = await interceptor(processedResponse, config);
            } catch (error) {
                this.logger.warn('Erro em interceptor de response', { error: error.message });
            }
        }
        
        return processedResponse;
    }
    
    /**
     * Adiciona interceptor de request
     */
    addRequestInterceptor(interceptor) {
        if (typeof interceptor !== 'function') {
            throw new Error('Interceptor deve ser uma função');
        }
        
        this.requestInterceptors.push(interceptor);
        this.logger.debug('Interceptor de request adicionado');
    }
    
    /**
     * Adiciona interceptor de response
     */
    addResponseInterceptor(interceptor) {
        if (typeof interceptor !== 'function') {
            throw new Error('Interceptor deve ser uma função');
        }
        
        this.responseInterceptors.push(interceptor);
        this.logger.debug('Interceptor de response adicionado');
    }
    
    /**
     * Remove interceptor de request
     */
    removeRequestInterceptor(interceptor) {
        const index = this.requestInterceptors.indexOf(interceptor);
        if (index > -1) {
            this.requestInterceptors.splice(index, 1);
            this.logger.debug('Interceptor de request removido');
        }
    }
    
    /**
     * Remove interceptor de response
     */
    removeResponseInterceptor(interceptor) {
        const index = this.responseInterceptors.indexOf(interceptor);
        if (index > -1) {
            this.responseInterceptors.splice(index, 1);
            this.logger.debug('Interceptor de response removido');
        }
    }
    
    /**
     * Testa conectividade
     */
    async testConnectivity() {
        try {
            const startTime = performance.now();
            const response = await this.request('GET', `${this.config.apiUrl}/health`, null, {
                timeout: 5000,
                retry: false
            });
            const duration = performance.now() - startTime;
            
            return {
                connected: true,
                latency: Math.round(duration),
                status: response.status,
                timestamp: Date.now()
            };
            
        } catch (error) {
            return {
                connected: false,
                error: error.message,
                timestamp: Date.now()
            };
        }
    }
    
    /**
     * Gera ID único para requisição
     */
    generateRequestId() {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `req-${timestamp}-${random}`;
    }
    
    /**
     * Atualiza estatísticas
     */
    updateStats(duration, success, bytesTransferred) {
        if (success) {
            this.stats.requestsSuccessful++;
        } else {
            this.stats.requestsFailed++;
        }
        
        // Atualiza tempo médio de resposta
        const totalRequests = this.stats.requestsSuccessful + this.stats.requestsFailed;
        this.stats.averageResponseTime = (
            (this.stats.averageResponseTime * (totalRequests - 1) + duration) / totalRequests
        );
        
        this.stats.lastRequestTime = Date.now();
        this.stats.bytesTransferred += bytesTransferred;
    }
    
    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            requestsSent: this.stats.requestsSent,
            requestsSuccessful: this.stats.requestsSuccessful,
            requestsFailed: this.stats.requestsFailed,
            successRate: this.stats.requestsSent > 0 ? 
                (this.stats.requestsSuccessful / this.stats.requestsSent * 100).toFixed(2) + '%' : '0%',
            averageResponseTime: Math.round(this.stats.averageResponseTime),
            lastRequestTime: this.stats.lastRequestTime,
            retryAttempts: this.stats.retryAttempts,
            bytesTransferred: this.stats.bytesTransferred,
            queueSize: this.requestQueue.length,
            isProcessingQueue: this.isProcessingQueue
        };
    }
    
    /**
     * Limpa fila de requisições
     */
    clearQueue() {
        const queueSize = this.requestQueue.length;
        
        // Rejeita todas as requisições pendentes
        this.requestQueue.forEach(request => {
            request.reject(new Error('Fila de requisições limpa'));
        });
        
        this.requestQueue = [];
        this.logger.info('Fila de requisições limpa', { queueSize });
    }
    
    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        
        // Atualiza headers padrão
        this.defaultHeaders = {
            ...this.defaultHeaders,
            'X-Customer-ID': this.config.customerId
        };
        
        // Atualiza configurações de retry e timeout
        this.maxRetries = this.config.maxRetries || this.maxRetries;
        this.retryDelay = this.config.retryDelay || this.retryDelay;
        this.timeout = this.config.timeout || this.timeout;
        this.maxQueueSize = this.config.maxQueueSize || this.maxQueueSize;
        
        this.logger.debug('Configuração do HttpService atualizada');
    }
    
    /**
     * Utilitário para sleep
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    /**
     * Testa suporte do serviço
     */
    static isSupported() {
        return !!(window.fetch && window.AbortController);
    }
    
    /**
     * Obtém capacidades do serviço
     */
    static getCapabilities() {
        return {
            supported: HttpService.isSupported(),
            features: {
                fetch: !!window.fetch,
                abortController: !!window.AbortController,
                requestInterceptors: true,
                responseInterceptors: true,
                retryLogic: true,
                queueing: true
            }
        };
    }
    
    /**
     * Limpa recursos
     */
    destroy() {
        // Limpa fila de requisições
        this.clearQueue();
        
        // Limpa interceptors
        this.requestInterceptors = [];
        this.responseInterceptors = [];
        
        this.logger.debug('HttpService destruído');
    }
}